/**
 * Purpose: default Generate-image dialog prompts so Gemini/OpenAI keep the
 *          designed storey stack and plan shapes.
 *
 * Input:  geometry format (json / screenshot / both), map camera, site, and
 *         a BuildingDesign massing summary.
 * Output: free-text system prompt (massing + rules) and description (look / mood).
 * Consumers: Generate image dialog in map capture and report cover.
 */

import {
  BUILDING_RENDER_GEOMETRY_RULES,
  BUILDING_RENDER_JSON_CHECKLIST,
  BUILDING_RENDER_JSON_CONSTRUCTION,
  BUILDING_RENDER_SCREENSHOT_TRACE,
} from '@shared/helpers/design/building-design-render-prompt.helpers';

export const PROMPT_STRINGS = {
  TASK: 'Task: Generate exactly one photorealistic exterior of THIS massing — every building shown, a tracing with real materials, not a new design.',

  GEOMETRY_RULES: BUILDING_RENDER_GEOMETRY_RULES,

  SCREENSHOT_TRACE: BUILDING_RENDER_SCREENSHOT_TRACE,

  JSON_CHECKLIST: BUILDING_RENDER_JSON_CHECKLIST,

  JSON_CONSTRUCTION: BUILDING_RENDER_JSON_CONSTRUCTION,

  JSON_FIDELITY_CHECK:
    'Final check: the building bodies must be the JSON prisms — as boxy as those polygons. Extra building bodies, floors, or a plinth under a building are wrong. Roof is flat unless the user description asks for a slope. Ground is flat.',

  TRACE_FIDELITY_CHECK:
    "Final check: the photorealistic image must be a tracing of the map massing. Same outline, each building's own floor-band count, no extra wings or utbyggnader, nothing extra under the building. Roof is a flat slab unless the user description asks for a slope. Sit it on flat ground. If you added a floor, a basement, or a volume that is not in the screenshot, the image is wrong.",

  SWEDISH_CONTEXT:
    'Swedish context: materials and daylight may follow Boverket / SMHI, but never by changing the massing.',

  INPUT_JSON_ONLY:
    'A geometry JSON is attached with a legend. Those prisms are the only building bodies. Extrude each buildings[].storeys[].shapes polygon from elevation_m by height_m. Keep each body as boxy as its polygons. Do not invent extra building bodies, floors, or wings, and do not merge separate buildings into one block.',

  INPUT_SCREENSHOT_ONLY:
    "Trace the attached {mode} map screenshot. The 3D massing in that image is the site. Keep every building, that exact silhouette, and each building's own storey count. Photorealism is a skin, not a new design.",

  INPUT_COMBINED:
    'Trace the attached {mode} map screenshot. The 3D boxes in that image ARE the site massing — same camera, same silhouette, same storey stacks, every separate building. Replace map-style shading with photorealistic façades; do not redraw the form.\nThe geometry JSON is a checklist for storey count per building and no extra masses. Do not reconstruct a different building from the coordinates.',

  // Description field: look and mood only. Geometry and massing stay in the
  // system prompt so regular users never have to write those instructions.
  DESCRIPTION_DEFAULT:
    'Scandinavian materials, realistic daylight, natural sky.',

  SITE_AND_ROOF:
    'Site and roof: sit every building on flat, level ground (no slope, berm, podium, or hill). Do not add a storey, plinth, garage, crawl space, or extra floor area under a building unless it is specified. Roof is always a flat slab on the top-storey outline unless the user description explicitly asks for a sloping or pitched roof.',

  STYLE_SCOPE:
    'The user description is look and mood (materials, daylight). It may add balconies. It may ask for a sloping roof; otherwise the roof stays flat. It must not add floors, a basement, building bodies, wings, or annexes. Match each storey program (shops vs housing vs office) and floor-to-floor height.',

  USER_PROMPT_MACRO: '{{user-prompt}}',
  JSON_MACRO: '{{json}}',
} as const;
